import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { ArrowRightIcon } from "styles/icons";
import { useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import { Fade, Flex, Text, Button, Box, CloseButton } from "@chakra-ui/react";
import { StickyBottomBarAhPlusLogo } from "./StickyBottomBarAhPlusLogo";
import { stickyBottomBarMobileStyles } from "./StickyBottomBarMobile.styles";
import { useDynamicIndex } from "./StickyBottomBarMobile.hook";
import type { StickyBottomBarProps } from "../StickyBottomBar.d";
import { getCTAElementProps } from "../utils/getCTAElementProps";
import { BenefitIcon } from "../components/BenefitIcon";

const StickyBottomBarMobile = ({ variant, heading, benefits, buttonText }: StickyBottomBarProps) => {
  const language = useLanguage();
  const index = useDynamicIndex(benefits?.length);
  const translations = useTranslations();
  const { closeBottomBar } = useStickyBottomBarContext();
  const CTAElementProps = getCTAElementProps(language, variant);
  const showBenefits = benefits?.length > 0;
  const designVariant = variant === "basic" ? "basic" : "ahPlus";

  const actionButton = {
    basic: showBenefits && (
      <Box sx={stickyBottomBarMobileStyles[designVariant].actionButton} pointerEvents="none">
        <Fade in key={benefits[index].textKey}>
          <Text color="greyscale.100" fontSize="sm" fontWeight="bold">
            {benefits[index].text || translations(benefits[index].textKey)}
            <ArrowRightIcon ml="6px" />
          </Text>
        </Fade>
      </Box>
    ),
    ahPlus: (
      <Button size="xs" sx={stickyBottomBarMobileStyles[designVariant].actionButton} pointerEvents="none">
        <ArrowRightIcon />
      </Button>
    ),
  }[designVariant];

  const customHeading = {
    basic: (
      <>
        {buttonText || translations("check_compensation")} {!showBenefits && <ArrowRightIcon ml="6px" />}
      </>
    ),
    ahPlus: heading || translations("be_prepared_for_future_flight_disruptions"),
  }[designVariant];

  return (
    <Flex
      align="flex-start"
      px={4}
      py={5}
      borderRadius="lg"
      mx={4}
      mb={4}
      sx={stickyBottomBarMobileStyles[designVariant].container}
    >
      <Flex as="a" sx={stickyBottomBarMobileStyles[designVariant].benefits} {...CTAElementProps}>
        {showBenefits && (
          <Fade in key={benefits[index].textKey}>
            <Flex borderRadius="50%" border="2px solid" borderColor="primary.400" mr={3} p={2}>
              <BenefitIcon icon={benefits[index].icon} />
            </Flex>
          </Fade>
        )}
      </Flex>
      <Flex as="a" sx={stickyBottomBarMobileStyles[designVariant].innerContainer} {...CTAElementProps}>
        <StickyBottomBarAhPlusLogo mr={3} sx={stickyBottomBarMobileStyles[designVariant].logo} />
        <Text as="span" fontWeight={700} sx={stickyBottomBarMobileStyles[designVariant].heading}>
          {customHeading}
        </Text>
        {actionButton}
      </Flex>
      <CloseButton
        sx={stickyBottomBarMobileStyles[designVariant].closeButton}
        onClick={closeBottomBar}
        aria-label="Close bottom bar"
      />
    </Flex>
  );
};

export default StickyBottomBarMobile;
